import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useLazyGetConversationCreativeMediaProgressQuery, type ConversationCreativeMediaProgressInput } from "./creativeMediaApi";
import { creativeMediaSampleConflict, isCreativeMediaRunnable, mergeCreativeMediaProgressSamples } from "./creativeMediaProgress";
import { CreativeMediaProgressScheduler } from "./creativeMediaProgressScheduler";

export function useConversationCreativeMediaProgress(input: ConversationCreativeMediaProgressInput, aiActive: boolean, enabled = true) {
  const [read, query] = useLazyGetConversationCreativeMediaProgressQuery();
  const scheduler = useRef<CreativeMediaProgressScheduler | null>(null);
  const scopeKey = JSON.stringify([input.userId, input.organizationId, input.conversationId]);
  const generation = useRef(0);
  const [clock, setClock] = useState(() => ({ scopeKey, value: performance.now() }));
  const displayedClock = useRef<{ scopeKey: string; value: number } | null>(null);
  const [failureClock, setFailureClock] = useState<{ scopeKey: string; value: number } | null>(null);
  const lastDiscovery = useRef("");
  const manualRefresh = useRef(false);
  const queryScope = query.originalArgs;
  const current = enabled && queryScope?.userId === input.userId && queryScope.organizationId === input.organizationId &&
    queryScope.conversationId === input.conversationId;
  const samples = current ? query.currentData?.samples ?? [] : [];
  const aborted = query.error && typeof query.error === "object" && "name" in query.error && query.error.name === "AbortError";
  const failed = current && ((query.isError && !aborted) || query.currentData?.protocolError === true || failureClock?.scopeKey === scopeKey);
  const discoveryKey = JSON.stringify([input.originTurnIds, input.jobIds, aiActive]);
  const readLatest = useEffectEvent((epoch: number) => {
    lastDiscovery.current = discoveryKey;
    const knownJobs = samples.filter((sample) => manualRefresh.current || isCreativeMediaRunnable(sample.job.status)).map((sample) => sample.job.job_id);
    manualRefresh.current = false;
    const request = read({ ...input, jobIds: [...new Set([...input.jobIds, ...knownJobs])] });
    return {
      abort: () => request.abort(),
      settled: request.unwrap().then((result) => {
        if (epoch !== generation.current || document.hidden) return { continuePolling: false };
        if (result.protocolError === true || result.samples.some((incoming) => {
          const previous = samples.find((sample) => sample.job.job_id === incoming.job.job_id);
          return previous && creativeMediaSampleConflict(previous, incoming);
        })) {
          setFailureClock((previous) => previous?.scopeKey === scopeKey ? previous :
            displayedClock.current?.scopeKey === scopeKey ? displayedClock.current : { scopeKey, value: performance.now() });
          return { continuePolling: true, failed: true };
        }
        setFailureClock(null);
        const nextClock = { scopeKey, value: performance.now() };
        displayedClock.current = nextClock;
        setClock(nextClock);
        return { continuePolling: aiActive || mergeCreativeMediaProgressSamples(samples, result.samples).some((sample) => isCreativeMediaRunnable(sample.job.status)) };
      }, (error: unknown) => {
        if (epoch !== generation.current || document.hidden) return { continuePolling: false };
        // StrictMode 的旧请求可能正在 settle；可见的新代次只读重校准，不标陈旧。
        if (error && typeof error === "object" && "name" in error && error.name === "AbortError") return { continuePolling: true };
        setFailureClock((previous) => previous?.scopeKey === scopeKey ? previous :
          displayedClock.current?.scopeKey === scopeKey ? displayedClock.current : { scopeKey, value: performance.now() });
        const status = error && typeof error === "object" && "status" in error ? error.status : null;
        return { continuePolling: ![401, 403, 404].includes(Number(status)), failed: true };
      }),
    };
  });
  useEffect(() => {
    if (!enabled) return;
    const epoch = ++generation.current;
    const controller = new CreativeMediaProgressScheduler(() => readLatest(epoch));
    scheduler.current = controller;
    if (document.hidden) controller.pause();
    else controller.requestNow();
    const visibility = () => document.hidden ? controller.pause() : controller.resume();
    const refresh = () => { if (!document.hidden) controller.requestNow(); };
    document.addEventListener("visibilitychange", visibility);
    window.addEventListener("online", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      scheduler.current = null;
      manualRefresh.current = false;
      generation.current += 1;
      controller.dispose();
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("online", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [enabled, scopeKey]);
  useEffect(() => {
    if (!enabled) return;
    if (lastDiscovery.current === discoveryKey) return;
    lastDiscovery.current = discoveryKey;
    scheduler.current?.requestNow();
  }, [discoveryKey, enabled]);
  const hasRunnable = samples.some((sample) => isCreativeMediaRunnable(sample.job.status));
  useEffect(() => {
    if (!hasRunnable || failed) return;
    const epoch = generation.current;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (!document.hidden && epoch === generation.current) {
        const nextClock = { scopeKey, value: performance.now() };
        displayedClock.current = nextClock;
        setClock(nextClock);
      }
      timer = setTimeout(tick, 1000);
    };
    timer = setTimeout(tick, 1000);
    return () => clearTimeout(timer);
  }, [failed, hasRunnable, scopeKey]);
  return { samples, failed, isLoading: current && query.isLoading,
    monotonicNow: failed && failureClock?.scopeKey === scopeKey ? failureClock.value :
      clock.scopeKey === scopeKey ? clock.value : 0,
    retryRead: () => { manualRefresh.current = true; scheduler.current?.requestNow(); } };
}
